import { FeaturesType } from 'contexts';
import {
  TenantId,
  TenantIds,
  ZOOCASA_TENANT_ID,
  EXP_REALTY_CA_TENANT_ID,
  EXP_REALTY_US_TENANT_ID,
  EXP_IN_SERVICE_TENANT_ID,
  EXP_COMMERCIAL_TENANT_ID,
  EXP_COMMERCIAL_CA_TENANT_ID,
} from '../types/tenant';
import {
  ThemeNames,
} from '../types/theme';
import { getTenantFromFeatureFlag } from '../manager';

//#region Theme Checks
export const isThemeExpCA = (theme: ThemeNames) => theme === ThemeNames.EXP_REALTY_CA;
export const isThemeExpUS = (theme: ThemeNames) => theme === ThemeNames.EXP_REALTY_US;
export const isThemeExpInService = (theme: ThemeNames) => theme === ThemeNames.EXP_IN_SERVICE;
export const isThemeZoocasa = (theme: ThemeNames) => theme === ThemeNames.ZOOCASA;
export const isThemeExpCommercial = (theme: ThemeNames) => theme === ThemeNames.EXP_COMMERCIAL;
export const isExpTheme = (theme: ThemeNames) => isThemeExpCA(theme) || isThemeExpUS(theme) || isThemeExpInService(theme) || isThemeExpCommercial(theme);
//#endregion

//#region Tenant Impersonation Checks
const isImpersonatingExpCA = (features: FeaturesType) => getTenantFromFeatureFlag(features)?.id === EXP_REALTY_CA_TENANT_ID;
const isImpersonatingExpUS = (features: FeaturesType) => getTenantFromFeatureFlag(features)?.id === EXP_REALTY_US_TENANT_ID;
const isImpersonatingExpInService = (features: FeaturesType) => getTenantFromFeatureFlag(features)?.id === EXP_IN_SERVICE_TENANT_ID;
const isImpersonatingZoocasa = (features: FeaturesType) => getTenantFromFeatureFlag(features)?.id === ZOOCASA_TENANT_ID;
const isImpersonatingExpCommercial = (features: FeaturesType) => getTenantFromFeatureFlag(features)?.id === EXP_COMMERCIAL_TENANT_ID;
const isImpersonatingExpCommercialCa = (features: FeaturesType) => getTenantFromFeatureFlag(features)?.id === EXP_COMMERCIAL_CA_TENANT_ID;
//#endregion

//#region Tenant Checks

export const isTenantExpCA = (tenantId: TenantId, features?: FeaturesType) => isImpersonatingExpCA(features) || tenantId === EXP_REALTY_CA_TENANT_ID;
export const isTenantExpUS = (tenantId: TenantId, features?: FeaturesType) => isImpersonatingExpUS(features) || tenantId === EXP_REALTY_US_TENANT_ID;
export const isTenantExpCommercial = (tenantId: TenantId, features?: FeaturesType) => isImpersonatingExpCommercial(features) || tenantId === EXP_COMMERCIAL_TENANT_ID;
export const isTenantExpCommercialCa = (tenantId: TenantId, features?: FeaturesType) => isImpersonatingExpCommercialCa(features) || tenantId === EXP_COMMERCIAL_CA_TENANT_ID;
export const isTenantExpInService = (tenantId: TenantId, features?: FeaturesType) => isImpersonatingExpInService(features) || tenantId === EXP_IN_SERVICE_TENANT_ID;
export const isZoocasaTenant = (tenantId: TenantId, features?: FeaturesType) => isImpersonatingZoocasa(features) || tenantId === ZOOCASA_TENANT_ID;

export const isExpTenant = (tenantId: TenantId, features?: FeaturesType) => isTenantExpCA(tenantId, features) || isTenantExpUS(tenantId, features) || isTenantExpCommercial(tenantId, features) || isTenantExpCommercialCa(tenantId, features) || isTenantExpInService(tenantId, features);
//#endregion

//#region Conversion Functions

/**
 * Converts a theme name to a tenant ID.
 *
 * This is required because the theme name is not the same as the tenant ID for EXP_REALTY_CA and EXP_REALTY_US.
 *
 * @param themeName The theme name to convert.
 * @returns The tenant ID.
 */
export function themeNameToTenantId(themeName: ThemeNames): TenantId {
  switch (themeName) {
    case ThemeNames.EXP_REALTY_CA:
      return TenantIds.EXP_REALTY_CA;
    case ThemeNames.EXP_REALTY_US:
      return TenantIds.EXP_REALTY_US;
    default:
      return themeName;
  }
}

/**
 * Converts a tenant ID to a theme name.
 *
 * This is required because the theme name is not the same as the tenant ID for EXP_REALTY_CA and EXP_REALTY_US.
 *
 * @param tenantId The tenant ID to convert.
 * @returns The theme name.
 */
export function tenantIdToThemeName(tenantId: TenantId): ThemeNames {
  switch (tenantId) {
    case EXP_REALTY_CA_TENANT_ID:
      return ThemeNames.EXP_REALTY_CA;
    case EXP_REALTY_US_TENANT_ID:
      return ThemeNames.EXP_REALTY_US;
    default:
      return tenantId;
  }
}

//#endregion